import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';

const STAGES = [
  { key: 'concept',           label: 'Concept',           color: '#6B7280' },
  { key: 'design',            label: 'Design',            color: '#3B82F6' },
  { key: 'prototype',         label: 'Prototype',         color: '#8B5CF6' },
  { key: 'testing',           label: 'Testing',           color: '#F59E0B' },
  { key: 'demonstration',     label: 'Demonstration',     color: '#EA580C' },
  { key: 'commercialization', label: 'Commercialization', color: '#c4a563' },
  { key: 'fielded',           label: 'Fielded',           color: '#10B981' },
  { key: 'shelved',           label: 'Shelved',           color: '#374151' },
];

const OPP_STAGE_COLORS = {
  identify: '#6B7280', qualify: '#3B82F6', develop: '#8B5CF6', capture: '#F59E0B',
  propose: '#EA580C', negotiate: '#EF4444', won: '#10B981', lost: '#374151',
};

const NEED_TYPES = [
  { key: 'technical_hire', label: 'Technical Hire' },
  { key: 'equipment',      label: 'Equipment' },
  { key: 'facility',       label: 'Facility' },
  { key: 'funding',        label: 'Funding' },
  { key: 'partner',        label: 'Partner' },
  { key: 'other',          label: 'Other' },
];

const PRIORITY_COLORS = { 1: '#EF4444', 2: '#F59E0B', 3: '#EAB308', 4: '#6B7280', 5: '#4B5563' };

const formatM = (v) => {
  if (!v) return null;
  const n = parseFloat(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n}`;
};

const stageMeta = (key) => STAGES.find(s => s.key === key) || STAGES[0];

// Auto-PSC classification (keep in sync with seeds/psc_mapping_rules.json domains).
const PSC_DOMAINS = [
  ['SENSORS_RADAR', 'Radar & RF Sensing'],
  ['SENSORS_SONAR', 'Sonar & Underwater Acoustics'],
  ['SENSORS_LIDAR_EO_IR', 'Lidar / EO / IR'],
  ['CYBER_DEFENSIVE', 'Defensive Cyber'],
  ['CYBER_OFFENSIVE', 'Offensive Cyber'],
  ['SOFTWARE_APPDEV', 'Software / App Development'],
  ['SOFTWARE_SAAS_CLOUD', 'SaaS / Cloud'],
  ['UNMANNED_AIR', 'Unmanned Aircraft (UAS)'],
  ['UNMANNED_GROUND', 'Unmanned Ground (UGV)'],
  ['UNMANNED_MARITIME', 'Autonomous Maritime (USV/UUV)'],
  ['SVC_PROGRAM_MGMT', 'Program Management Services'],
  ['SVC_ENGINEERING', 'Engineering & Technical Services'],
];
const OPERATING_INTENTS = ['ANY', 'AIRBORNE', 'GROUND', 'MARITIME', 'SPACE', 'CYBER', 'FIXED_SITE'];
const PSC_STAGE_NAMES = { 1: 'Basic Research', 2: 'Applied Research', 3: 'Experimental Development', 4: 'R&D Admin', 5: 'R&D Facilities' };
const GOV_OFFICE_LABELS = {
  SCIENCE_TECH: 'S&T', RESEARCH_DEV: 'R&D', ACQUISITION: 'Acquisition',
  SUSTAINMENT: 'Sustainment', MAINTENANCE: 'Maintenance', SERVICES: 'Services',
};
const LIFECYCLE_STATUS_COLOR = { past: '#4B5563', current: '#22D3EE', upcoming: '#64748B' };
const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: 4, fontSize: 12, width: '100%', boxSizing: 'border-box' };
const lbl = { color: '#94A3B8', fontSize: 11, display: 'block', marginBottom: 3 };

function TimelineBar({ start, target }) {
  if (!start || !target) return null;
  const s = new Date(start).getTime(), t = new Date(target).getTime(), now = Date.now();
  if (t <= s) return null;
  const pct = Math.max(0, Math.min(100, ((now - s) / (t - s)) * 100));
  const overdue = now > t;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 9, color: '#4B5563' }}>{new Date(start).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}</span>
      <div style={{ flex: 1, height: 4, background: '#0A0F1C', borderRadius: 2, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: overdue ? '#EF4444' : '#22D3EE' }} />
      </div>
      <span style={{ fontSize: 9, color: overdue ? '#EF4444' : '#4B5563' }}>{new Date(target).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}</span>
    </div>
  );
}

// Searchable PSC (Product/Service Code) picker — tags a dev effort to component-level PSCs so it
// can be positioned against the competitive landscape on the same taxonomy as the award data.
function PscPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const rows = await crmService.searchPSC(q.trim());
        if (alive) { setResults(rows); setOpen(true); }
      } catch { if (alive) setResults([]); }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const add = (r) => {
    if (!value.some(p => p.code === r.code)) {
      onChange([...value, { code: r.code, title: r.title, family: r.family }]);
    }
    setQ(''); setResults([]); setOpen(false);
  };
  const remove = (code) => onChange(value.filter(p => p.code !== code));

  const inpS = { width: '100%', background: '#0D1117', border: '1px solid #1E2D45', color: '#F1F5F9', borderRadius: 4, padding: '6px 8px', fontSize: 12 };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {value.map(p => (
          <span key={p.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 10, fontSize: 10, background: '#22D3EE18', color: '#22D3EE', border: '1px solid #22D3EE44' }}>
            <strong>{p.code}</strong> {(p.title || '').slice(0, 34)}
            <button type="button" onClick={() => remove(p.code)} style={{ background: 'none', border: 'none', color: '#22D3EE', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>×</button>
          </span>
        ))}
      </div>
      <input
        style={inpS}
        placeholder="Search PSC code or name (e.g. radar, 5840, engines)…"
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (loading || results.length > 0) && (
        <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: 2, background: '#0D1117', border: '1px solid #1E2D45', borderRadius: 4, maxHeight: 220, overflowY: 'auto' }}>
          {loading && <div style={{ padding: '6px 10px', color: '#6B7280', fontSize: 11 }}>Searching…</div>}
          {results.map(r => (
            <div key={r.code} onMouseDown={() => add(r)}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid #111827', color: '#E2E8F0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <strong style={{ color: '#22D3EE' }}>{r.code}</strong> {r.title}
              <span style={{ color: '#64748B', marginLeft: 6 }}>· {r.family}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectModal({ project, verticals, onClose, onSaved }) {
  const isEdit = !!project?.id;
  const [form, setForm] = useState(() => ({
    name: project?.name || '', description: project?.description || '',
    stage: project?.stage || 'concept', status: project?.status || 'active',
    start_date: project?.start_date ? project.start_date.slice(0, 10) : '',
    target_date: project?.target_date ? project.target_date.slice(0, 10) : '',
    priority: project?.priority || 3, notes: project?.notes || '',
    vertical_ids: (project?.verticals || []).map(v => v.id),
    psc_codes: (project?.psc_codes || []).map(p => ({ ...p })),
    milestones: (project?.milestones || []).map(m => ({ ...m })),
    trl_level: project?.trl_level || '',
    operating_intent: project?.operating_intent || '',
    classification_domain: project?.classification_domain || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleVertical = (id) => set('vertical_ids',
    form.vertical_ids.includes(id) ? form.vertical_ids.filter(x => x !== id) : [...form.vertical_ids, id]);

  const setMilestone = (i, k, v) => {
    const ms = form.milestones.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    set('milestones', ms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        start_date: form.start_date || null,
        target_date: form.target_date || null,
        milestones: form.milestones.filter(m => (m.label || '').trim()),
      };
      const saved = isEdit
        ? await crmService.updateTechProject(project.id, payload)
        : await crmService.createTechProject(payload);
      onSaved(saved);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, width: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#22D3EE' }}>{isEdit ? 'Edit Development Effort' : 'New Development Effort'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Effort Name *</label>
            <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            <div>
              <label style={lbl}>Stage</label>
              <select style={inp} value={form.stage} onChange={e => set('stage', e.target.value)}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Status</label>
              <select style={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                {['active', 'on_hold', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Start</label>
              <input type="date" style={inp} value={form.start_date} onChange={e => set('start_date', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Target</label>
              <input type="date" style={inp} value={form.target_date} onChange={e => set('target_date', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Description</label>
            <textarea style={{ ...inp, height: 56, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Technology Verticals</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {verticals.map(v => {
                const on = form.vertical_ids.includes(v.id);
                return (
                  <button key={v.id} type="button" onClick={() => toggleVertical(v.id)} style={{
                    padding: '3px 10px', borderRadius: 10, fontSize: 10, cursor: 'pointer',
                    background: on ? '#22D3EE18' : 'transparent', color: on ? '#22D3EE' : '#6B7280',
                    border: `1px solid ${on ? '#22D3EE44' : '#1E2D45'}`, fontWeight: on ? 600 : 400,
                  }}>{v.name}</button>
                );
              })}
            </div>
          </div>
          <div style={{ marginTop: 12, padding: '10px 12px', border: '1px solid #1E2D45', borderRadius: 6, background: '#0D1117' }}>
            <label style={{ ...lbl, color: '#22D3EE' }}>Auto-PSC classification <span style={{ color: '#64748B', fontWeight: 400 }}>— TRL + domain drive PSC/FSC codes automatically on save</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
              <div>
                <label style={lbl}>Domain</label>
                <select style={inp} value={form.classification_domain} onChange={e => set('classification_domain', e.target.value)}>
                  <option value="">— none —</option>
                  {PSC_DOMAINS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>TRL</label>
                <select style={inp} value={form.trl_level} onChange={e => set('trl_level', e.target.value ? parseInt(e.target.value) : '')}>
                  <option value="">— derive from stage —</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <option key={n} value={n}>TRL {n}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Operating intent</label>
                <select style={inp} value={form.operating_intent} onChange={e => set('operating_intent', e.target.value)}>
                  <option value="">ANY</option>
                  {OPERATING_INTENTS.filter(i => i !== 'ANY').map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>PSC codes <span style={{ color: '#64748B', fontWeight: 400 }}>— auto-filled by classification above; you can also tag components manually</span></label>
            <PscPicker value={form.psc_codes} onChange={v => set('psc_codes', v)} />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>Milestones</label>
            {form.milestones.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={!!m.done} onChange={e => setMilestone(i, 'done', e.target.checked)} />
                <input style={{ ...inp, flex: 1 }} placeholder="Milestone" value={m.label || ''} onChange={e => setMilestone(i, 'label', e.target.value)} />
                <input type="date" style={{ ...inp, width: 140 }} value={m.date || ''} onChange={e => setMilestone(i, 'date', e.target.value)} />
                <button type="button" onClick={() => set('milestones', form.milestones.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => set('milestones', [...form.milestones, { label: '', date: '', done: false }])}
              style={{ background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 11 }}>
              + Milestone
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, height: 48, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </form>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '6px 14px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, opportunities, onEdit, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [needForm, setNeedForm] = useState(null); // { need_type, description, priority }
  const [linkOppId, setLinkOppId] = useState('');
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null); // { psc_codes, local_matches, ... }
  const [matchBusy, setMatchBusy] = useState(false);

  const reclassify = async () => {
    setBusy(true);
    try { const r = await crmService.reclassifyTechProject(project.id); if (r && r.classified === false) alert('Set a Domain (and TRL or Stage) to auto-classify.'); await onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const findMatches = async () => {
    setMatchBusy(true);
    try { setMatches(await crmService.getTechProjectOpportunityMatches(project.id)); }
    catch (e) { alert(e.message); } finally { setMatchBusy(false); }
  };

  const meta = stageMeta(project.stage);
  const needs = project.needs || [];
  const openNeeds = needs.filter(n => ['open', 'in_review'].includes(n.status));
  const ms = project.milestones || [];
  const msDone = ms.filter(m => m.done).length;
  const nextMs = ms.filter(m => !m.done && m.date).sort((a, b) => a.date.localeCompare(b.date))[0];
  const linkedOpps = project.opportunities || [];
  const linkable = opportunities.filter(o => !['won', 'lost'].includes(o.stage) && !linkedOpps.some(l => l.id === o.id));

  const saveNeed = async () => {
    if (!needForm?.description?.trim()) return;
    setBusy(true);
    try {
      await crmService.addTechNeed(project.id, needForm);
      setNeedForm(null);
      await onChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const promote = async (needId) => {
    setBusy(true);
    try { await crmService.promoteTechNeed(needId); await onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const setNeedStatus = async (needId, status) => {
    try { await crmService.updateTechNeed(needId, { status }); await onChanged(); }
    catch (e) { alert(e.message); }
  };

  const linkOpp = async () => {
    if (!linkOppId) return;
    setBusy(true);
    try { await crmService.linkTechProjectOpportunity(project.id, parseInt(linkOppId)); setLinkOppId(''); await onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
      {/* Header row */}
      <div onClick={() => setExpanded(x => !x)} style={{ padding: '12px 16px', cursor: 'pointer', borderLeft: `3px solid ${meta.color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_COLORS[project.priority] || '#6B7280', flexShrink: 0 }} />
          <span style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 600 }}>{project.name}</span>
          <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 8, border: `1px solid ${meta.color}55`, color: meta.color, fontWeight: 600 }}>{meta.label}</span>
          {project.status !== 'active' && (
            <span style={{ fontSize: 10, color: '#6B7280' }}>{project.status.replace('_', ' ')}</span>
          )}
          {(project.verticals || []).slice(0, 3).map(v => (
            <span key={v.id} style={{ background: '#22D3EE0F', color: '#22D3EE', border: '1px solid #22D3EE25', padding: '1px 7px', borderRadius: 8, fontSize: 9 }}>{v.name}</span>
          ))}
          {project.classification_domain && project.psc_stage_digit && (
            <span title={`Auto-classified: ${PSC_STAGE_NAMES[project.psc_stage_digit] || ''}${project.operating_intent ? ' · ' + project.operating_intent : ''}${project.gov_office ? ' · buyer: ' + (GOV_OFFICE_LABELS[project.gov_office] || project.gov_office) : ''}`}
              style={{ background: '#22D3EE14', color: '#22D3EE', border: '1px solid #22D3EE33', padding: '1px 7px', borderRadius: 8, fontSize: 9, fontWeight: 600 }}>
              {project.trl_level ? `TRL ${project.trl_level}` : 'AUTO'}{project.gov_office ? ` · ${GOV_OFFICE_LABELS[project.gov_office] || project.gov_office}` : ` · ${PSC_STAGE_NAMES[project.psc_stage_digit] || 'classified'}`}
            </span>
          )}
          {(project.psc_codes || []).slice(0, 4).map(p => (
            <span key={p.code} title={p.title} style={{ background: '#c4a56312', color: '#c4a563', border: '1px solid #c4a56333', padding: '1px 6px', borderRadius: 8, fontSize: 9, fontFamily: 'monospace' }}>{p.code}</span>
          ))}
          {openNeeds.length > 0 && (
            <span style={{ background: '#c4a56318', color: '#c4a563', border: '1px solid #c4a56344', padding: '1px 8px', borderRadius: 8, fontSize: 9, fontWeight: 700 }}>
              {openNeeds.length} need{openNeeds.length !== 1 ? 's' : ''}
            </span>
          )}
          {(project.team || []).length > 0 ? (
            <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
              {(project.team || []).slice(0, 4).map(t => t.avatar_url ? (
                <img key={t.member_id} src={t.avatar_url} alt="" title={`${t.name}${t.role ? ` — ${t.role}` : ''} (${t.allocation_pct}%)`} style={{
                  width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', border: '1px solid #22D3EE33',
                }} />
              ) : (
                <span key={t.member_id} title={`${t.name}${t.role ? ` — ${t.role}` : ''} (${t.allocation_pct}%)`} style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#0A0F1C', border: '1px solid #22D3EE33',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, color: '#22D3EE', fontWeight: 700,
                }}>
                  {(t.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </span>
              ))}
              {(project.team || []).length > 4 && <span style={{ fontSize: 9, color: '#4B5563' }}>+{project.team.length - 4}</span>}
            </span>
          ) : project.status === 'active' ? (
            <span style={{ background: 'transparent', color: '#c4a563', border: '1px dashed #c4a56344', padding: '1px 8px', borderRadius: 8, fontSize: 9, fontWeight: 700 }}>
              UNSTAFFED
            </span>
          ) : null}
          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6B7280' }}>
            {ms.length > 0 ? `${msDone}/${ms.length} milestones` : ''}
            {nextMs ? ` · next: ${nextMs.label} (${new Date(nextMs.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})` : ''}
          </span>
          <span style={{ color: '#4B5563', fontSize: 11 }}>{expanded ? '▾' : '▸'}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 8 }}>
          <div style={{ flex: 1, maxWidth: 320 }}>
            <TimelineBar start={project.start_date} target={project.target_date} />
          </div>
          {linkedOpps.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {linkedOpps.slice(0, 3).map(o => (
                <span key={o.id} style={{
                  fontSize: 9, padding: '1px 7px', borderRadius: 8,
                  border: `1px solid ${OPP_STAGE_COLORS[o.stage] || '#374151'}55`,
                  color: OPP_STAGE_COLORS[o.stage] || '#94A3B8',
                }}>
                  {o.name}{o.value_usd ? ` · ${formatM(o.value_usd)}` : ''}
                </span>
              ))}
              {linkedOpps.length > 3 && <span style={{ fontSize: 9, color: '#4B5563' }}>+{linkedOpps.length - 3}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div style={{ borderTop: '1px solid #1E2D45', padding: '12px 16px', background: '#0D1526' }}>
          {project.description && (
            <div style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6, marginBottom: 12 }}>{project.description}</div>
          )}

          {/* Federal classification & matching */}
          <div style={{ marginBottom: 12, padding: '8px 10px', border: '1px solid #1E2D45', borderRadius: 6, background: '#0D1117' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Federal PSC / FSC</span>
              {project.classification_domain
                ? <span style={{ fontSize: 10, color: '#22D3EE' }}>{project.classification_domain}{project.psc_stage_digit ? ` · ${PSC_STAGE_NAMES[project.psc_stage_digit]}` : ''}</span>
                : <span style={{ fontSize: 10, color: '#4B5563' }}>No domain set — edit to enable auto-classification</span>}
              <span style={{ flex: 1 }} />
              <button onClick={reclassify} disabled={busy}
                style={{ padding: '3px 10px', background: '#1E2D45', color: '#94A3B8', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Reclassify</button>
              <button onClick={findMatches} disabled={matchBusy}
                style={{ padding: '3px 10px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>{matchBusy ? 'Searching…' : 'Find opportunities'}</button>
            </div>
            {(project.psc_lifecycle || []).length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'stretch' }}>
                {project.psc_lifecycle.map((e, i) => (
                  <div key={e.office + i} title={`${e.label}${e.note ? ' — ' + e.note : ''} (TRL ${e.trl_min}-${e.trl_max}, ${e.status})`}
                    style={{
                      flex: '1 1 90px', minWidth: 90, padding: '5px 8px', borderRadius: 6,
                      background: e.status === 'current' ? '#22D3EE14' : '#0D1526',
                      border: `1px solid ${e.status === 'current' ? '#22D3EE55' : '#1E2D45'}`,
                      opacity: e.status === 'past' ? 0.55 : 1,
                    }}>
                    <div style={{ fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase', color: LIFECYCLE_STATUS_COLOR[e.status] || '#64748B' }}>
                      {GOV_OFFICE_LABELS[e.office] || e.office}{e.status === 'current' ? ' ◄' : ''}
                    </div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#c4a563', marginTop: 2 }}>{e.psc.code}</div>
                  </div>
                ))}
              </div>
            )}
            {matches && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, color: '#64748B', marginBottom: 4 }}>
                  Matching on {(matches.psc_codes || []).join(', ') || '—'} · {(matches.local_matches || []).length} imported solicitation{(matches.local_matches || []).length !== 1 ? 's' : ''}
                </div>
                {(matches.local_matches || []).slice(0, 8).map(m => (
                  <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0', fontSize: 11 }}>
                    <span style={{ color: '#c4a563', fontFamily: 'monospace', fontSize: 9 }}>{m.psc_code}</span>
                    <span style={{ color: '#F1F5F9', flex: 1 }}>{m.title}</span>
                    <span style={{ color: '#6B7280', fontSize: 9 }}>{m.agency}</span>
                  </div>
                ))}
                {(matches.local_matches || []).length === 0 && (
                  <div style={{ fontSize: 10, color: '#4B5563' }}>No imported solicitations on these codes yet. Live SAM.gov / USAspending query payloads are ready in the API response for a scheduled pull.</div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 18 }}>
            {/* Milestones */}
            <div>
              <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Milestones</div>
              {ms.length === 0 && <div style={{ fontSize: 11, color: '#374151' }}>None defined — edit the effort to add.</div>}
              {ms.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
                  <span style={{ color: m.done ? '#10B981' : '#4B5563', fontSize: 11 }}>{m.done ? '✓' : '○'}</span>
                  <span style={{ fontSize: 12, color: m.done ? '#6B7280' : '#F1F5F9', textDecoration: m.done ? 'line-through' : 'none', flex: 1 }}>{m.label}</span>
                  {m.date && <span style={{ fontSize: 10, color: '#6B7280' }}>{new Date(m.date).toLocaleDateString()}</span>}
                </div>
              ))}

              {(project.recent_updates || []).length > 0 && (
                <>
                  <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '12px 0 6px' }}>Recent Progress</div>
                  {(project.recent_updates || []).map(u => (
                    <div key={u.id} style={{ padding: '3px 0', fontSize: 11, lineHeight: 1.5 }}>
                      <span style={{ color: '#94A3B8' }}>{u.update_text}</span>
                      <span style={{ color: '#4B5563', fontSize: 9 }}> — {u.author || 'unknown'}, {new Date(u.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                    </div>
                  ))}
                </>
              )}

              <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '12px 0 6px' }}>Linked Pursuits (The Charge)</div>
              {linkedOpps.length === 0 && <div style={{ fontSize: 11, color: '#374151', marginBottom: 6 }}>No deals linked yet.</div>}
              {linkedOpps.map(o => (
                <div key={o.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
                  <span style={{ fontSize: 12, color: '#F1F5F9', flex: 1 }}>{o.name}</span>
                  <span style={{ fontSize: 10, color: OPP_STAGE_COLORS[o.stage] || '#94A3B8', textTransform: 'capitalize' }}>{o.stage}</span>
                  {o.value_usd && <span style={{ fontSize: 11, color: '#22D3EE', fontWeight: 600 }}>{formatM(o.value_usd)}</span>}
                  <button onClick={() => crmService.unlinkTechProjectOpportunity(project.id, o.id).then(onChanged)}
                    style={{ background: 'none', border: 'none', color: '#4B5563', cursor: 'pointer', fontSize: 12 }}>×</button>
                </div>
              ))}
              {linkable.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <select value={linkOppId} onChange={e => setLinkOppId(e.target.value)} style={{ ...inp, flex: 1 }}>
                    <option value="">Link a pipeline deal…</option>
                    {linkable.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <button onClick={linkOpp} disabled={!linkOppId || busy}
                    style={{ padding: '4px 12px', background: linkOppId ? '#22D3EE' : '#1E2D45', color: linkOppId ? '#0A0F1C' : '#4B5563', border: 'none', borderRadius: 4, cursor: linkOppId ? 'pointer' : 'default', fontSize: 11, fontWeight: 700 }}>Link</button>
                </div>
              )}
            </div>

            {/* Needs */}
            <div>
              <div style={{ fontSize: 9, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Development Needs</div>
              {needs.length === 0 && <div style={{ fontSize: 11, color: '#374151' }}>None logged.</div>}
              {needs.map(n => (
                <div key={n.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #11182766' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: PRIORITY_COLORS[n.priority] || '#6B7280', flexShrink: 0 }} />
                  <span style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', flexShrink: 0 }}>{(NEED_TYPES.find(t => t.key === n.need_type)?.label || n.need_type)}</span>
                  <span style={{ fontSize: 12, color: ['resolved', 'dropped'].includes(n.status) ? '#4B5563' : '#F1F5F9', flex: 1, textDecoration: n.status === 'dropped' ? 'line-through' : 'none' }}>{n.description}</span>
                  {n.growth_action_id ? (
                    <span style={{ fontSize: 9, color: '#c4a563', border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 7px', flexShrink: 0 }}>in Growth</span>
                  ) : ['open', 'in_review'].includes(n.status) ? (
                    <button onClick={() => promote(n.id)} disabled={busy} title="Send to Growth for strategic weighting"
                      style={{ background: '#c4a56318', border: '1px solid #c4a56344', color: '#c4a563', borderRadius: 8, padding: '1px 8px', cursor: 'pointer', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                      ↑ Growth
                    </button>
                  ) : null}
                  <select value={n.status} onChange={e => setNeedStatus(n.id, e.target.value)}
                    style={{ background: '#0A0F1C', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, fontSize: 10, padding: '2px 4px', flexShrink: 0 }}>
                    {['open', 'in_review', 'resolved', 'dropped'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>
              ))}
              {needForm ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <select value={needForm.need_type} onChange={e => setNeedForm(f => ({ ...f, need_type: e.target.value }))} style={{ ...inp, width: 130 }}>
                    {NEED_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  <input style={{ ...inp, flex: 1, minWidth: 160 }} placeholder="What's needed…" value={needForm.description}
                    onChange={e => setNeedForm(f => ({ ...f, description: e.target.value }))} />
                  <select value={needForm.priority} onChange={e => setNeedForm(f => ({ ...f, priority: parseInt(e.target.value) }))} style={{ ...inp, width: 64 }}>
                    {[1, 2, 3, 4, 5].map(p => <option key={p} value={p}>P{p}</option>)}
                  </select>
                  <button onClick={saveNeed} disabled={busy} style={{ padding: '4px 12px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Add</button>
                  <button onClick={() => setNeedForm(null)} style={{ padding: '4px 8px', background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>✕</button>
                </div>
              ) : (
                <button onClick={() => setNeedForm({ need_type: 'technical_hire', description: '', priority: 3 })}
                  style={{ marginTop: 8, background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}>
                  + Log Need
                </button>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => onEdit(project)} style={{ background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>Edit Effort</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TechDevelopment({ onNavigate }) {
  const [projects, setProjects] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { project? }

  const load = useCallback(() => crmService.getTechProjects().then(setProjects).catch(console.error), []);

  useEffect(() => {
    Promise.all([
      load(),
      crmService.getVerticals().then(setVerticals),
      crmService.getOpportunities({}).then(setOpportunities),
      crmService.getSupplyChainRiskSummary().then(setRisk).catch(() => setRisk(null)),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [load]);

  const active = projects.filter(p => p.status === 'active');
  const openNeeds = projects.flatMap(p => (p.needs || []).filter(n => ['open', 'in_review'].includes(n.status)));

  if (loading) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 40 }}>Sharpening the tusks…</div>;
  }

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1180, margin: '0 auto' }}>
      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#111827', border: '1px solid #1E2D45', borderTop: '2px solid #22D3EE', borderRadius: 8, padding: '14px 18px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Development Efforts</div>
          <div style={{ color: '#F1F5F9', fontSize: 24, fontWeight: 700 }}>{active.length}</div>
          <div style={{ color: '#4B5563', fontSize: 10, marginTop: 2 }}>{projects.length - active.length > 0 ? `${projects.length - active.length} inactive/completed` : 'all active'}</div>
        </div>
        <div style={{ background: '#111827', border: '1px solid #1E2D45', borderTop: '2px solid #c4a563', borderRadius: 8, padding: '14px 18px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Open Development Needs</div>
          <div style={{ color: '#F1F5F9', fontSize: 24, fontWeight: 700 }}>{openNeeds.length}</div>
          <div style={{ color: '#4B5563', fontSize: 10, marginTop: 2 }}>{openNeeds.filter(n => n.growth_action_id).length} already in Growth for weighting</div>
        </div>
        <div
          onClick={() => onNavigate?.('supply-chain')}
          style={{ background: '#111827', border: '1px solid #1E2D45', borderTop: `2px solid ${risk && risk.at_risk > 0 ? '#EF4444' : '#10B981'}`, borderRadius: 8, padding: '14px 18px', cursor: 'pointer' }}
        >
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Supply Chain Exposure</div>
          {risk ? (
            <>
              <div style={{ fontSize: 24, fontWeight: 700, color: risk.at_risk > 0 ? '#EF4444' : '#10B981' }}>
                {risk.at_risk}<span style={{ fontSize: 13, color: '#6B7280', fontWeight: 400 }}> of {risk.total} components at risk</span>
              </div>
              <div style={{ color: '#4B5563', fontSize: 10, marginTop: 2 }}>
                {risk.critical} critical shortage · {risk.high_risk} high risk{risk.adversary_sourced > 0 ? ` · ${risk.adversary_sourced} adversary-sourced` : ''} — view Sankey →
              </div>
            </>
          ) : (
            <div style={{ color: '#4B5563', fontSize: 12 }}>No component data — view Supply Chain →</div>
          )}
        </div>
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Internal Technology Development & Commercialization
        </div>
        <button onClick={() => setModal({})} style={{ marginLeft: 'auto', padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 'bold' }}>
          + New Effort
        </button>
      </div>

      {projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, color: '#6B7280' }}>
          <img src="/elephant-icon.svg" alt="" style={{ width: 56, height: 56, opacity: 0.2, marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: '#94A3B8', marginBottom: 6 }}>No development efforts yet</div>
          <div style={{ fontSize: 12, marginBottom: 14 }}>Track what the herd is building: stages, milestones, linked pursuits, and the needs that feed Growth.</div>
          <button onClick={() => setModal({})} style={{ padding: '8px 20px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            Start First Effort
          </button>
        </div>
      ) : (
        projects.map(p => (
          <ProjectCard key={p.id} project={p} opportunities={opportunities}
            onEdit={(proj) => setModal({ project: proj })} onChanged={load} />
        ))
      )}

      {modal && (
        <ProjectModal
          project={modal.project}
          verticals={verticals}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
